// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import CreateNewGroupModal from "@bg/App/Components/MyCustomQuotes/CreateGroupModal";
import copyLines from "@bg/App/Handlers/CustomQuote/CopyCustomQuoteLines";
import deleteGroup from "@bg/App/Handlers/CustomQuote/DeleteGroup";
import moveLines from "@bg/App/Handlers/CustomQuote/MoveCustomQuoteLines";
import setSelectedLines from "@bg/App/Handlers/CustomQuote/SetSelectedLines";
import { CustomQuoteModel, GroupLineModel } from "@bg/App/Services/CustomQuoteService";
import { linkIconStyleGenerator } from "@bg/App/SharedStyles/GeneralStyles";
import { CustomAppState } from "@bg/App/Store/Reducers";
import TwoButtonModal from "@bg/Overrides/Components/TwoButtonModal";
import { fontConfig } from "@bg/XDL/PreStyle/typography";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { API_URL_CURRENT_FRAGMENT } from "@insite/client-framework/Services/ApiService";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import addLinesToCart from "@insite/client-framework/Store/Pages/Cart/Handlers/AddLinesToCart";
import translate from "@insite/client-framework/Translate";
import { CartLineCollectionModel, CartLineModel } from "@insite/client-framework/Types/ApiModels";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Checkbox, { CheckboxProps } from "@insite/mobius/Checkbox";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useContext, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface GroupActionsProps {
    listGroups: string[];
    currentGroup: string;
    quote: CustomQuoteModel;
}

const mapStateToProps = (state: CustomAppState, ownProps: GroupActionsProps) => ({
    selectedLineIds: state.myCustomQuoteDetails.selectedLineIds[ownProps.currentGroup],
    uncategorizedGroupName: (getSettingsCollection(state).customSettings as any).unCategorizedGroupName as string,
    addOnsGroupName: (getSettingsCollection(state).customSettings as any).addOnsGroupName as string,
});

const mapDispatchToProps = {
    addLinesToCart,
    setSelectedLines,
    deleteGroup,
    copyLines,
    moveLines,
};

type Props = GroupActionsProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface GroupActionsStyles {
    selectSortOption?: SelectPresentationProps;
    deleteGroupLink?: LinkPresentationProps;
    groupAddToCartButton?: ButtonPresentationProps;
    groupAcionsWrapper?: InjectableCss;
    selectAllGroup?: InjectableCss;
    copyToGroup?: InjectableCss;
    moveToGroup?: InjectableCss;
    deleteGroup?: InjectableCss;
    addGroupToCart?: InjectableCss;
}

export const styles: GroupActionsStyles = {
    deleteGroupLink: linkIconStyleGenerator({
        icon: "Delete",
        textSize: "15px",
        iconSize: 16,
        additionalTypographyCss: css`
            opacity: 0.7;
            font-weight: ${fontConfig.weight.semiBold};
        `,
    }),
    groupAddToCartButton: {
        sizeVariant: "small",
        css: css`
            &&&,
            &&&:disabled {
                background: ${getColor("primary")};
                color: ${getColor("common.white")};
                border: 2px solid ${getColor("primary")};
            }
            &&&:hover:not(:disabled) {
                background: ${getColor("common.white")};
                color: ${getColor("primary")};
            }
        `,
    },
    groupAcionsWrapper: {
        css: css`
            display: flex;
            align-items: center;
            flex-wrap: wrap;
            padding: 15px;
            border-right: 3px solid ${getColor("common.ui3")};
            border-left: 3px solid ${getColor("common.ui3")};
            @media print {
                display: none;
            }
            @media (max-width: 481px) {
                justify-content: space-between;
            }
        `,
    },
    selectAllGroup: {
        css: css`
            margin-right: 30px;
            @media (max-width: 481px) {
                margin-bottom: 20px;
            }
        `,
    },
    copyToGroup: {
        css: css`
            margin-right: 30px;
            &&.disabled svg,
            &&.disabled label {
                opacity: 0.5;
            }
            @media (max-width: 788px) {
                margin-top: 10px;
                margin-right: 30px;
            }
            @media (max-width: 481px) {
                width: 100%;
            }
        `,
    },
    moveToGroup: {
        css: css`
            display: flex;
            margin-right: 30px;
            &&.disabled svg,
            &&.disabled label {
                opacity: 0.5;
            }
            @media (max-width: 481px) {
                width: 100%;
                margin-right: 0;
                margin-bottom: 20px;
            }
            svg {
                margin-left: 5px;
            }
        `,
    },
    selectSortOption: {
        cssOverrides: {
            formField: css`
                flex-direction: row;
                align-items: center;
                width: 235px;

                label {
                    width: auto;
                    font-weight: ${fontConfig.weight.bold};
                    font-size: ${fontConfig.fontSize.small}px;
                    margin-right: 8px;
                    white-space: nowrap;
                }

                &&&& select:disabled {
                    color: ${getColor("text.main")};
                    opacity: 0.5;
                }

                @media (max-width: 481px) {
                    width: 100%;
                }
            `,
            formInputWrapper: css`
                margin-top: 0 !important;
            `,
            inputSelect: css`
                font-size: ${fontConfig.fontSize.small}px;
                height: 32px;
            `,
        },
        iconProps: {
            css: css`
                &&&&&& {
                    right: 0;
                    top: 0;
                }
            `,
        },
    },
    addGroupToCart: {
        css: css`
            flex-grow: 1;
            display: flex;
            justify-content: flex-end;
            @media (max-width: 1093px) {
                justify-content: flex-start;
                margin-top: 10px;
            }
        `,
    },
    deleteGroup: {
        css: css`
            @media (max-width: 853px) {
                margin-top: 10px;
                margin-right: 30px;
            }
            @media (max-width: 481px) {
                order: 10;
                margin: 0;
                display: flex;
                margin-top: 10px;
                && button span + span {
                    display: none;
                }
            }
            &&.disabled {
                pointer-events: none;
                cursor: not-allowed;
                opacity: 0.5;
            }
        `,
    },
};

const MyCustomQuoteDetailsGroupActions: React.FC<Props> = ({
    currentGroup,
    listGroups,
    selectedLineIds,
    quote,
    addLinesToCart,
    uncategorizedGroupName,
    addOnsGroupName,
    setSelectedLines,
    deleteGroup,
    moveLines,
    copyLines,
}) => {
    const toasterContext = useContext(ToasterContext);
    const [isAllSelected, setIsAllSelected] = useState(false);
    const [moveToGroup, setMoveToGroup] = useState("Choose a Group");
    const [copyToGroup, setCopyToGroup] = useState("Choose a Group");
    const [isDisabled, setIsDisabled] = useState(false);
    const [isMoveCopyDisabled, setIsMoveCopyDisabled] = useState(false);
    const [groupingModalIsOpen, setGroupingModalIsOpen] = useState(false);
    const [isMove, setIsMove] = useState(false);
    const [deleteGroupModalIsOpen, setDeleteGroupModalIsOpen] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isMovingLine, setIsMovingLine] = useState(false);
    const [isCopyingLine, setIsCopyingLine] = useState(false);

    useEffect(() => {
        if (quote.groupLinesModel && !quote.groupLinesModel[currentGroup]?.length) {
            setIsDisabled(true);
        } else {
            setIsDisabled(false);
        }
        if (!selectedLineIds || (selectedLineIds && !selectedLineIds?.length)) {
            setIsMoveCopyDisabled(true);
        } else {
            setIsMoveCopyDisabled(false);
        }
    }, [quote, selectedLineIds]);

    useEffect(() => {
        if (selectedLineIds) {
            const lineIds: string[] = [];
            quote.groupLinesModel[currentGroup] &&
                quote.groupLinesModel[currentGroup].forEach((line: any) => lineIds.push(line.lineId));

            if (
                quote.groupLinesModel[currentGroup] &&
                quote.groupLinesModel[currentGroup].every((line: any) => selectedLineIds.indexOf(line.lineId) > -1)
            ) {
                setIsAllSelected(true);
            } else {
                setIsAllSelected(false);
            }
        } else {
            setIsAllSelected(false);
        }
    }, [selectedLineIds]);

    const selectAllChangeHandler: CheckboxProps["onChange"] = (_, value) => {
        setSelectedLines({ selectAll: value, groupName: currentGroup, clearAll: !value });
    };

    // move item to another group
    const handleSelectMoveToGroup = (e: React.FormEvent<HTMLSelectElement>) => {
        setIsMove(true);
        const newGroup = e.currentTarget.value;
        setMoveToGroup(newGroup);
        if (newGroup === "Create a New Group") {
            setIsCreatingNewGroup();
        } else {
            handleCopyOrMove(newGroup, true);
        }
    };

    // copy item to another group
    const handleSelectCopyToGroup = (e: React.FormEvent<HTMLSelectElement>) => {
        setIsMove(false);
        const newGroup = e.currentTarget.value;
        setCopyToGroup(newGroup);

        if (newGroup === "Create a New Group") {
            setIsCreatingNewGroup();
        } else {
            handleCopyOrMove(newGroup, false);
        }
    };

    // shared functionality for copy and move
    const handleCopyOrMove = (newGroup: string, isMoving?: boolean) => {
        if (newGroup !== "Choose a Group" || !newGroup) {
            if (selectedLineIds && selectedLineIds.length) {
                if (isMoving) {
                    const linesModel = [] as { lineId: string; sortOrder: number }[];
                    selectedLineIds.forEach((id: string) => {
                        const line = quote.groupLinesModel[currentGroup].filter(line => line.lineId === id)?.[0];
                        linesModel.push({ lineId: line.lineId, sortOrder: line.sortOrder });
                    });
                    setIsMovingLine(true);

                    moveLines({
                        customQuoteId: quote.id,
                        newGroupName: newGroup,
                        linesModel,
                        onComplete: (result: any) => {
                            setIsMovingLine(false);

                            if (result.successful) {
                                setSelectedLines({
                                    lineIds: [...selectedLineIds],
                                    groupName: currentGroup,
                                    clearAll: true,
                                });
                            }
                        },
                        onError: error => {
                            toasterContext.addToast({ body: translate(error), messageType: "danger" });
                            setIsMovingLine(false);
                        },
                    });
                } else {
                    setIsCopyingLine(true);

                    copyLines({
                        customQuoteId: quote.id,
                        newGroupName: newGroup,
                        linesIds: [...selectedLineIds],
                        onComplete: (result: any) => {
                            setIsCopyingLine(false);
                        },
                        onError: error => {
                            toasterContext.addToast({ body: translate(error), messageType: "danger" });
                            setIsCopyingLine(false);
                        },
                    });
                }
            }
        }
        setCopyToGroup("Choose a Group");
        setMoveToGroup("Choose a Group");
    };

    const setIsCreatingNewGroup = () => {
        setGroupingModalIsOpen(true);
    };

    const modalCloseHandler = () => {
        setGroupingModalIsOpen(false);
        setCopyToGroup("Choose a Group");
        setMoveToGroup("Choose a Group");
    };

    // delete group ->
    const deleteGroupHandler = () => {
        setIsDeleting(true);

        deleteGroup({
            customQuoteId: quote.id,
            groupName: currentGroup,
            onComplete: () => {
                setIsDeleting(false);
                setDeleteGroupModalIsOpen(false);
            },
        });
    };

    const addGroupToCart = () => {
        const cartLines: CartLineModel[] = [];
        if (quote.groupLinesModel[currentGroup]) {
            quote.groupLinesModel[currentGroup].forEach(line => {
                const cartLine = {
                    productId: line.productResult.id,
                    unitOfMeasure: line.productResult.unitOfMeasures?.[0]?.unitOfMeasure,
                    qtyOrdered: line.qty,
                    notes: line.notes,
                } as any;
                cartLines.push(cartLine);
                if (line.relatedItems && line.relatedItems.length > 0) {
                    line.relatedItems.forEach(related => {
                        const cartLine = {
                            productId: related.productResult.id,
                            unitOfMeasure: related.productResult.unitOfMeasures?.[0]?.unitOfMeasure,
                            qtyOrdered: related.qty,
                            notes: related.notes,
                        } as any;
                        cartLines.push(cartLine);
                    });
                }
            });

            addLinesToCart({
                apiParameter: {
                    cartId: API_URL_CURRENT_FRAGMENT,
                    cartLineCollection: { cartLines } as CartLineCollectionModel,
                },
                onSuccess: onAddToCartSuccess,
                onComplete(resultProps) {
                    if (resultProps.apiResult) {
                        // eslint-disable-next-line react/no-this-in-sfc
                        this.onSuccess?.();
                    }
                },
            });
        }
    };

    const onAddToCartSuccess = () => {
        toasterContext.addToast({
            body: translate("{0} Added to Cart", currentGroup),
            messageType: "success",
        });
    };

    const copyMoveOptions = ["Choose a Group", ...listGroups, "Create a New Group"];

    return (
        <StyledWrapper {...styles.groupAcionsWrapper}>
            <StyledWrapper {...styles.selectAllGroup}>
                <Checkbox
                    checked={isAllSelected}
                    onChange={selectAllChangeHandler}
                    data-test-selector="selectAllGroupItems"
                    disabled={isDisabled}
                >
                    {translate("Select All Products")}
                </Checkbox>
            </StyledWrapper>
            <StyledWrapper {...styles.moveToGroup} className={isDisabled || isMoveCopyDisabled ? "disabled" : ""}>
                <Select
                    label={translate("Move to")}
                    {...styles.selectSortOption}
                    value={moveToGroup}
                    disabled={isDisabled || isMoveCopyDisabled}
                    onChange={handleSelectMoveToGroup}
                    data-test-selector="myListDetails_GroupMoveTo"
                >
                    {copyMoveOptions.length > 0 &&
                        copyMoveOptions
                            .filter(i => i.toLowerCase() !== currentGroup.toLowerCase())
                            .map(option => (
                                <option value={option} key={`${option}_${currentGroup}`}>
                                    {option}
                                </option>
                            ))}
                </Select>
                {isMovingLine && <LoadingSpinner size={28} color="secondary" />}
            </StyledWrapper>
            <StyledWrapper {...styles.moveToGroup} className={isDisabled || isMoveCopyDisabled ? "disabled" : ""}>
                <Select
                    label={translate("Copy to")}
                    {...styles.selectSortOption}
                    value={copyToGroup}
                    disabled={isDisabled || isMoveCopyDisabled}
                    onChange={handleSelectCopyToGroup}
                    data-test-selector="myListDetails_GroupCopyTo"
                >
                    {copyMoveOptions.length > 0 &&
                        copyMoveOptions
                            .filter(i => i.toLowerCase() !== currentGroup.toLowerCase())
                            .map(option => (
                                <option value={option} key={`${option}_${currentGroup}`}>
                                    {option}
                                </option>
                            ))}
                </Select>
                {isCopyingLine && <LoadingSpinner size={28} color="secondary" />}
            </StyledWrapper>
            {currentGroup.toLowerCase() !== uncategorizedGroupName.toLowerCase() &&
                currentGroup.toLowerCase() !== addOnsGroupName.toLowerCase() && (
                    <StyledWrapper {...styles.deleteGroup}>
                        <Link onClick={() => setDeleteGroupModalIsOpen(true)} {...styles.deleteGroupLink}>
                            {translate("Delete Group")}
                        </Link>
                    </StyledWrapper>
                )}
            <StyledWrapper {...styles.addGroupToCart}>
                {!quote.isCameFromNormalQuote && (
                    <Button {...styles.groupAddToCartButton} disabled={isDisabled} onClick={addGroupToCart}>
                        {translate("Add Group To Cart")}
                    </Button>
                )}
            </StyledWrapper>
            <CreateNewGroupModal
                closeModalCallback={modalCloseHandler}
                callAfterSuccess={handleCopyOrMove}
                creatingNewGroup={true}
                groupingModalIsOpen={groupingModalIsOpen}
                isMovingItem={isMove}
                quote={quote}
            />
            <TwoButtonModal
                modalIsOpen={deleteGroupModalIsOpen}
                headlineText={translate("Delete Group")}
                messageText={`${translate("Are you sure you want to delete this group?")}`}
                cancelButtonText={translate("Cancel")}
                submitButtonText={translate("Delete")}
                onCancel={() => setDeleteGroupModalIsOpen(false)}
                onSubmit={deleteGroupHandler}
                submitTestSelector="submitDeleteListItem"
                showSubmitBtnLoadingOn={isDeleting}
            />
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(MyCustomQuoteDetailsGroupActions);
